import type { LinkProps } from 'react-router-dom';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Breakpoints } from '@/styles/breakpoints';
import theme from '@shared/theme';
import { getPreviousTextSize } from './Text';
import { weightMap } from '@shared/weightMap';

const variants = {
    primary: css`
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.uNavy};
        border-color: ${({ theme }) => theme.colors.uNavy};
        gap: 8px;
        align-items: center;

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    secondary: css`
        background-color: ${({ theme }) => theme.colors.white};
        color: ${({ theme }) => theme.colors.navy};
        border-color: ${({ theme }) => theme.colors.white};
        align-items: center;
        gap: 8px;

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    tertiary: css`
        background-color: ${({ theme }) => theme.colors.lightNavy};
        color: ${({ theme }) => theme.colors.white};
        border-color: ${({ theme }) => theme.colors.lightNavy};
        align-items: center;
        gap: 8px;
        padding: 10px 16px;

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    line: css`
        background-color: transparent;
        color: ${({ theme }) => theme.colors.white};
        border-color: ${({ theme }) => theme.colors.white};
        font-weight: normal;
        gap: 8px;
        padding: 6px 12px;

        @media (min-width: ${Breakpoints.Medium}) {
            gap: 12px;
            padding: 8px 16px;
        }

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    slim: css`
        background-color: transparent;
        color: ${({ theme }) => theme.colors.white};
        border-color: ${({ theme }) => theme.colors.white};
        font-weight: normal;
        padding: 6px 12px;
        gap: 8px;
        align-items: center;

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    icon: css`
        border-color: transparent;
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.uNavy};
        gap: 8px;
        padding: 6px 12px;
        align-items: center;

        @media (min-width: ${Breakpoints.Medium}) {
            gap: 12px;
            padding: 8px 16px;
        }

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    tertiaryIcon: css`
        background-color: ${({ theme }) => theme.colors.lightNavy};
        color: ${({ theme }) => theme.colors.white};
        border-color: ${({ theme }) => theme.colors.lightNavy};
        gap: 8px;
        padding: 6px 12px;
        font-weight: bold;
        align-items: center;

        @media (min-width: ${Breakpoints.Medium}) {
            gap: 12px;
            padding: 8px 16px;
        }

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    jazzyIcon: css`
        color: ${({ theme }) => theme.colors.white};
        gap: 8px;
        padding: 6px 12px;
        align-items: center;
        position: relative;
        border: none;
        background-color: transparent;

        &::before {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 24px;
            padding: 2px;
            background: linear-gradient(
                45deg,
                #ffffff 0%,
                #ffffff 22%,
                #ffac90 50%,
                #ffffff 78%,
                #ffffff 100%
            );
            mask: linear-gradient(#000 0 0) content-box,
                linear-gradient(#000 0 0);
            mask-composite: exclude;
            transition: background-position 300ms;
        }

        @media (min-width: ${Breakpoints.Medium}) {
            gap: 12px;
            padding: 8px 16px;
        }

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);

            &::before {
                background-position: 50px;
            }
        }
    `,
    lineIcon: css`
        background-color: ${({ theme }) => theme.colors.uNavy05};
        color: ${({ theme }) => theme.colors.uNavy};
        border-color: ${({ theme }) => theme.colors.uNavy10};
        gap: 8px;
        padding: 6px 12px;
        align-items: center;

        @media (min-width: ${Breakpoints.Medium}) {
            gap: 12px;
            padding: 8px 16px;
        }

        &:hover {
            box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
        }
    `,
    styleless: css`
        background: 0;
        padding: 0;
        border: 0;
    `,
};
const variantStyles = (variant: keyof typeof variants = 'primary') =>
    variants[variant];

export type ButtonProps = {
    $variant?: keyof typeof variants;
    $color?: keyof typeof theme.colors;
    $borderColor?: keyof typeof theme.colors;
    $backgroundColor?: keyof typeof theme.colors;
    $disabled?: boolean;
    disabled?: boolean;
    size?: keyof typeof theme.sizing;
    weight?: keyof typeof weightMap;

    $useSmartMobileSizing?: boolean;
};

export const Button = styled.button<ButtonProps>`
    appearance: none;
    border: 1px solid;
    display: inline-flex;
    justify-content: center;
    border-radius: 8px;
    padding: 8px 16px;
    transition: all 0.2s;
    white-space: nowrap;
    text-decoration: none;
    flex-shrink: 0;
    ${({ $variant }) => variantStyles($variant)};

    font-size: ${({ size, theme }) =>
        typeof size === 'number'
            ? `${size}px`
            : theme.sizing[size || 'medium'].size};
    line-height: ${({ size, theme }) =>
        theme.sizing[(size as keyof typeof theme.sizing) || 'medium']
            .lineHeight};
    font-weight: ${({ weight }) => weightMap[weight || 'medium']};

    ${({ $useSmartMobileSizing = true, size }) => {
        if (!$useSmartMobileSizing) return;

        return `
            @media (max-width: ${Breakpoints.Medium}) {
                font-size: ${
                    theme.sizing[getPreviousTextSize(size || 'medium')].size
                };
                line-height: ${
                    theme.sizing[getPreviousTextSize(size || 'medium')]
                        .lineHeight
                };
            }
        `;
    }}

    // overrides
    ${({ $color, theme }) =>
        $color &&
        css`
            color: ${theme.colors[$color]};
        `};

    ${({ $backgroundColor, theme }) =>
        $backgroundColor &&
        css`
            background-color: ${theme.colors[$backgroundColor]};
        `};

    ${({ $borderColor, theme }) =>
        $borderColor &&
        css`
            border-color: ${theme.colors[$borderColor]};
        `}

    &:hover {
        cursor: pointer;
    }

    ${({ $disabled, disabled }) =>
        ($disabled || disabled) && 'pointer-events: none; opacity: 0.5;'}
`;

type ButtonLinkProps = ButtonProps &
    LinkProps & {
        /** Accepted for source-compatibility with the old Remix Link; ignored. */
        prefetch?: 'intent' | 'render' | 'none' | 'viewport';
    };

const StyledButtonLink = styled(Button)`
    text-decoration: none;
`;

export const ButtonLink = ({ prefetch: _prefetch, ...props }: ButtonLinkProps) => (
    <StyledButtonLink as={Link} {...props} />
);
